/* eslint-disable react/prop-types */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getUnreadSummary } from '@/services/BtsService';
import { useDebouncedCallback } from '@/utils/useDebouncedCallback';

const BTSChatContext = createContext();

export const useBTSChatContext = () => {
  const context = useContext(BTSChatContext);
  if (!context) {
    throw new Error('useBTSChatContext must be used within a BTSChatProvider');
  }
  return context;
};

export const BTSChatProvider = ({ children }) => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'
  const [search, setSearch] = useState('');
  const [searchVal, setSearchVal] = useState(''); // Holds immediate value for input
  const [expandedSchoolCode, setExpandedSchoolCode] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Maintain refs to prevent closures from holding stale values in async callbacks
  const schoolsRef = useRef([]);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  const filterRef = useRef('all');
  const searchRef = useRef('');

  useEffect(() => { schoolsRef.current = schools; }, [schools]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);
  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { filterRef.current = filter; }, [filter]);
  useEffect(() => { searchRef.current = search; }, [search]);

  // Main fetch function - supports resetting (page 1) or appending (pagination)
  const fetchSchools = useCallback(async (isReset = false, showLoader = true) => {
    if (isReset) {
      if (showLoader) setLoading(true);
      loadingRef.current = true;
    } else {
      if (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current) return;
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }

    try {
      const lastSchool = !isReset && schoolsRef.current.length > 0
        ? schoolsRef.current[schoolsRef.current.length - 1].schoolCode
        : undefined;

      const data = await getUnreadSummary({
        limit: 10,
        search: searchRef.current || undefined,
        lastSchool,
        filter: filterRef.current,
      });

      if (Array.isArray(data)) {
        const currentFilter = filterRef.current;
        
        setSchools((prevSchools) => {
          const processItems = (listToFilterAgainst) => {
            if (currentFilter === 'unread') {
              return data.filter(
                (item) =>
                  item.totalUnreadCount > 0 ||
                  listToFilterAgainst.some((s) => s.schoolCode === item.schoolCode)
              );
            }
            return data;
          };

          return isReset ? processItems([]) : [...prevSchools, ...processItems(prevSchools)];
        });

        setHasMore(data.length === 10);
      } else {
        if (isReset) setSchools([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to fetch school unread summaries', error);
      if (isReset) setSchools([]);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingRef.current = false;
      loadingMoreRef.current = false;
    }
  }, []);

  // Fetch initial list when filter or search query changes
  useEffect(() => {
    fetchSchools(true, true);
  }, [filter, search, fetchSchools]);

  // Handle filter changes (All / Unread)
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setSearch('');
    setSearchVal('');
    setExpandedSchoolCode(null);
    setSchools([]);
  };

  // Debounced search setter
  const { call: debouncedSetSearch } = useDebouncedCallback((val) => {
    setSearch(val);
    setExpandedSchoolCode(null);
  }, 300);

  const handleSearchChange = (val) => {
    setSearchVal(val);
    debouncedSetSearch(val);
  };

  // Fetch the next page of schools (infinite scroll down)
  const fetchNextPage = useCallback(() => {
    fetchSchools(false, false);
  }, [fetchSchools]);

  // Background refresh to update unread counts in-place
  const refreshList = useCallback(async () => {
    try {
      const currentLimit = Math.max(schoolsRef.current.length, 10);
      const data = await getUnreadSummary({
        limit: currentLimit,
        search: searchRef.current || undefined,
        filter: filterRef.current,
      });
      if (Array.isArray(data)) {
        setSchools((prevSchools) => {
          return prevSchools.map((existing) => {
            const updated = data.find((item) => item.schoolCode === existing.schoolCode);
            if (updated) {
              return updated;
            } else {
              return {
                ...existing,
                totalUnreadCount: 0,
                classes: existing.classes
                  ? existing.classes.map((c) => ({ ...c, unreadCount: 0 }))
                  : [],
              };
            }
          });
        });
      }
    } catch (e) {
      console.error('Failed to refresh unread summary list', e);
    }
  }, []);
  // Local state update for message previews when sending messages
  const updateLastMessageLocally = useCallback((schoolCode, classId, lastMsgText) => {
    setSchools((prevSchools) => {
      return prevSchools.map((school) => {
        if (school.schoolCode === schoolCode) {
          const updatedClasses = school.classes
            ? school.classes.map((c) => {
                if (c.classId === classId) {
                  return {
                    ...c,
                    lastMessage: lastMsgText,
                    latestMessageTime: new Date().toISOString(),
                  };
                }
                return c;
              })
            : [];
          return {
            ...school,
            lastMessage: lastMsgText,
            latestMessageTime: new Date().toISOString(),
            classes: updatedClasses,
          };
        }
        return school;
      });
    });
  }, []);

  return (
    <BTSChatContext.Provider
      value={{
        schools,
        loading,
        loadingMore,
        filter,
        setFilter: handleFilterChange,
        searchQuery: searchVal,
        setSearchQuery: handleSearchChange,
        hasMore,
        expandedSchoolCode,
        setExpandedSchoolCode,
        selectedClass,
        setSelectedClass,
        fetchNextPage,
        refreshList,
        updateLastMessageLocally,
      }}
    >
      {children}
    </BTSChatContext.Provider>
  );
};
